import pluralize from 'pluralize';
import { Link } from 'react-router-dom';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { isHiringManagerOnly } from '@/modules/auth/utils/hasRoles';
import { CompanyClientStatusBadge } from '@/modules/companies/components/CompanyClientStatusBadge';
import type { JobApi } from '@/modules/jobs/types';
import { Avatar, AvatarFallback } from '@/modules/ui/components/avatar/Avatar';
import { Badge } from '@/modules/ui/components/badge/Badge';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Separator } from '@/modules/ui/components/separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { formatDateLabel, isRecent } from '@/utils/dateUtils';
import { getInitials } from '@/utils/getInitials';

import { JobDropdown } from './JobDropdown';
import { JobStateTag } from './JobStateTag';
import { SourcingStatusTag } from './SourcingStatusTag';

interface JobCardProps {
  job: JobApi;
  linkTo?: string;
  showCompanyClientStatus?: boolean;
}

export const JobCard = ({
  job,
  linkTo,
  showCompanyClientStatus = false,
}: JobCardProps) => {
  const cardLinkTo = linkTo || `/jobs/${job.id}/candidates`;
  const { data: currentUser } = useCurrentUserQuery();
  const isHM = isHiringManagerOnly(currentUser?.roles ?? []);

  return (
    <Card className="min-h-50 gap-2">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <Link to={cardLinkTo} className="flex items-center gap-2">
            <CardTitle>{job.title}</CardTitle>
            {isRecent(job.createdAt) && (
              <Badge
                variant="secondary"
                className="text-secondary-500 border-secondary-500 bg-[#CCE4E9] text-xs"
              >
                New
              </Badge>
            )}
          </Link>

          {!isHM && (
            <CardAction>
              <JobDropdown job={job} />
            </CardAction>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-1">
          <div className="flex min-w-0 items-center gap-2">
            <Paragraph variant="caption">
              <Link to={`/companies/${job.company.id}/edit`}>
                {job.company.name}
              </Link>
            </Paragraph>
            {showCompanyClientStatus && (
              <CompanyClientStatusBadge status={job.company.clientStatus} />
            )}
          </div>
          <Paragraph variant="caption" className="color-neutral-400">
            |
          </Paragraph>
          <Tooltip>
            <TooltipTrigger asChild>
              <Paragraph variant="caption" className="max-w-30 truncate">
                {job.location}
              </Paragraph>
            </TooltipTrigger>
            <TooltipContent side="bottom">{job.location}</TooltipContent>
          </Tooltip>

          <Paragraph variant="caption" className="color-neutral-400">
            |
          </Paragraph>
          <Paragraph variant="caption">
            {formatDateLabel(job.createdAt, 'Created')}
          </Paragraph>
        </div>

        <Separator orientation="horizontal" className="my-4 h-4" />

        <div className="flex flex-col gap-4">
          {!isHM && (
            <div className="flex items-center justify-between gap-2">
              <Paragraph variant="caption">Assigned to</Paragraph>

              {job.users && job.users.length > 0 ? (
                <div className="flex items-center">
                  {job.users?.map((user, index) => (
                    <Avatar
                      key={user.id}
                      className="size-6 cursor-pointer ring-2 ring-white transition-all duration-200 ease-in-out hover:z-10 hover:scale-110"
                      style={{
                        marginLeft: index > 0 ? '-0.3rem' : 0,
                        zIndex: index,
                      }}
                    >
                      <AvatarFallback
                        className="text-xs"
                        title={`${user.firstName} ${user.lastName}`}
                      >
                        {getInitials(`${user.firstName} ${user.lastName}`)}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                </div>
              ) : (
                <Paragraph variant="caption">Unassigned</Paragraph>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            {!isHM && (
              <PermissionsGate permissions={[permissions.Jobs.Update]}>
                <div className="flex flex-wrap items-center gap-2">
                  <JobStateTag state={job.state} />
                  <SourcingStatusTag
                    status={job.sourcingState}
                    lastSourcing={job.lastSourcing}
                  />
                </div>
              </PermissionsGate>
            )}
            <Paragraph variant="caption">
              {job.candidateCount}{' '}
              {pluralize('candidate', job.candidateCount ?? 0)}
            </Paragraph>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
